import { supabase } from './supabase';
import { generateSurveyTitle } from './surveyRanking';
import type {
  ProfileCompletion,
  UpsertAttributeRequest,
  RankedSurvey,
  PrequalifierQuestion,
  PrequalAnswer,
  PrequalSubmitResult,
  Reward,
  Redemption,
  PointsLedger,
  WelcomeSurveyQuestion,
} from '@swipeopinion/shared';
import { API_BASE } from './apiBase';

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

/**
 * Error carrying the API's machine-readable `code` so callers can branch on it
 * (e.g. the 403 `ACCOUNT_INELIGIBLE` an ejected member gets from the member-load
 * front door). A plain Error would drop the code and the caller could only show
 * a generic message.
 */
export class ApiError extends Error {
  code?: string;
  status?: number;
  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

/**
 * True when an error is the ejected-member signal (suspended/blocked/deleted).
 * Keyed STRICTLY on the `ACCOUNT_INELIGIBLE` code — NOT on any 403 and NOT on a
 * member status — so inactive / opted_out members (whom the API never 403s) can
 * never match this. Mirrors how the geo-block signal is detected off a specific
 * response field.
 */
export function isAccountIneligible(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'ACCOUNT_INELIGIBLE';
}

function handleResponse(res: Response): void {
  if (res.status === 401) {
    void supabase.auth.signOut();
    window.location.href = '/login?error=session_expired';
    throw new Error('Session expired');
  }
}

export async function fetchMe(): Promise<{ member: { id: string; email: string; total_points: number; status: string; role: string; display_name: string | null; avatar_id: string | null; created_at: string; streak_current: number; streak_longest: number; streak_last_activity_date: string | null }; points_balance: number; onboarding_complete: boolean; display_name: string | null; avatar_id: string | null; streak_current: number; streak_longest: number; streak_last_activity_date: string | null; email_survey_frequency: string; has_email?: boolean; children?: { birth_year: number; gender: string }[]; push_enabled: boolean; phone_number: string | null; phone_verified: boolean; sms_opted_in: boolean; email_verified: boolean; trust_score?: number; trust_level?: number; redemption_minimum_points?: number; points_pending?: number; points_available?: number; points_lifetime?: number; welcome_survey_completed?: boolean; identity_verified?: boolean; identity_verified_at?: string | null; identityVerificationAvailable?: boolean; identity_verification_status?: string | null }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/me`, { headers });
  handleResponse(res);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(err.error ?? 'Failed to initialize member', err.code, res.status);
  }
  return res.json() as Promise<{ member: { id: string; email: string; total_points: number; status: string; role: string; display_name: string | null; avatar_id: string | null; created_at: string; streak_current: number; streak_longest: number; streak_last_activity_date: string | null }; points_balance: number; onboarding_complete: boolean; display_name: string | null; avatar_id: string | null; streak_current: number; streak_longest: number; streak_last_activity_date: string | null; email_survey_frequency: string; has_email?: boolean; children?: { birth_year: number; gender: string }[]; push_enabled: boolean; phone_number: string | null; phone_verified: boolean; sms_opted_in: boolean; email_verified: boolean; trust_score?: number; trust_level?: number; redemption_minimum_points?: number; points_pending?: number; points_available?: number; points_lifetime?: number; welcome_survey_completed?: boolean }>;
}

export async function dailyCheckin(): Promise<{
  already_checked_in: boolean;
  points: number;
  streak: number;
  milestone: { days: number; points: number } | null;
}> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/member/daily-checkin`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to check in');
  }
  return res.json() as Promise<{
    already_checked_in: boolean;
    points: number;
    streak: number;
    milestone: { days: number; points: number } | null;
  }>;
}

export async function updateDisplayName(displayName: string): Promise<void> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/me/display-name`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ display_name: displayName }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to update display name');
  }
}

export async function updateAvatar(avatarId: string): Promise<void> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/me/avatar`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ avatar_id: avatarId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to update avatar');
  }
}

export async function fetchProfileAttributes(): Promise<{ key: string; value: string; source: string }[]> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/profile/attributes`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to fetch profile attributes');
  }
  return res.json() as Promise<{ key: string; value: string; source: string }[]>;
}

export async function upsertProfileAttribute(body: UpsertAttributeRequest): Promise<void> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/profile/attributes`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to save attribute');
  }
}

export async function fetchProfileCompletion(): Promise<ProfileCompletion> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/profile/completion`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to fetch completion');
  }
  return res.json() as Promise<ProfileCompletion>;
}

// ── Welcome survey (T9 — guaranteed first win) ───────────────────────────────

export async function fetchWelcomeSurvey(): Promise<{ available: boolean; points: number; questions: WelcomeSurveyQuestion[] }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/surveys/welcome`, { headers });
  if (!res.ok) throw new Error('Failed to load welcome survey');
  return res.json() as Promise<{ available: boolean; points: number; questions: WelcomeSurveyQuestion[] }>;
}

export async function completeWelcomeSurvey(): Promise<{ ok: boolean; points_awarded: number }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/surveys/welcome/complete`, { method: 'POST', headers });
  if (!res.ok) throw new Error('Failed to complete welcome survey');
  return res.json() as Promise<{ ok: boolean; points_awarded: number }>;
}

export async function fetchRankedSurveys(): Promise<RankedSurvey[]> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/surveys`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to fetch surveys');
  }
  const data = await res.json() as { surveys: Array<Record<string, unknown>> };

  // Map unified wall shape to the RankedSurvey shape frontend components consume.
  return data.surveys.map((s) => {
    const surveyId = String(s.survey_id ?? s.ps_survey_id ?? s.id ?? '');
    const loi = (s.loi as number) ?? (s.estimated_length_minutes as number) ?? 0;
    const rawName = (s.survey_name as string) ?? (s.title as string) ?? '';
    const isRfg = (s.exchange as string) === 'rfg';
    // RFG surveys: show the raw survey_name from the DB as-is. The friendly
    // pool fallback caused all RFG cards to collide on a handful of repeating
    // titles. Empty names fall back to a neutral "Survey" label.
    // TODO: ask RFG contact if a human-readable surveyTitle field is available
    // in the inventory API response — current poller only stores `name` /
    // `project_name`, which are often internal IDs.
    // PS surveys keep the existing auto-seeded-name → pool title behavior.
    let title: string;
    if (isRfg) {
      title = rawName !== '' ? rawName : 'Survey';
    } else {
      const isAutoSeededName = rawName === `Survey ${surveyId}`;
      title = isAutoSeededName || rawName === ''
        ? generateSurveyTitle(surveyId, loi || 10)
        : rawName;
    }
    return {
      id: surveyId,
      provider_id: String(s.survey_id ?? s.ps_survey_id ?? s.provider_id ?? ''),
      provider_name: (s.provider_name as string) ?? (s.exchange === 'rfg' ? 'Research For Good' : 'PureSpectrum'),
      title,
      description: (s.description as string) ?? null,
      category: (s.category as string) ?? null,
      survey_url: (s.survey_url as string) ?? null,
      points_value: (s.estimated_points as number) ?? (s.points_value as number) ?? 0,
      // Base-rate points for the L3+ elevated-payout strikethrough. Defaults to
      // the effective points (→ equal → no strikethrough) when absent (old cache).
      base_points: (s.base_points as number) ?? (s.estimated_points as number) ?? (s.points_value as number) ?? 0,
      consolation_points: (s.consolation_points as number) ?? 5,
      estimated_length_minutes: loi,
      status: (s.status as 'active') ?? 'active',
      created_at: s.created_at ? new Date(s.created_at as string) : new Date(),
      updated_at: s.updated_at ? new Date(s.updated_at as string) : new Date(),
      score: (s.score as number) ?? 0,
      pts_per_minute: (s.pts_per_minute as number) ?? 0,
      attribute_match_pct: (s.attribute_match_pct as number) ?? 0,
      prescreen: (s.requires_prescreen as boolean) ?? (s.prescreen as boolean) ?? false,
      isTest: (s.isTest as boolean) ?? false,
      unknownQuals: (s.unknownQuals as Array<{
        qualCode: number; question: string; type: string;
        options: Array<{ code: string; label: string }>;
        required_codes?: string[]; required_ranges?: Array<{ from: number; to: number }>;
        attributeKey?: string; exchange?: 'ps' | 'rfg';
      }>) ?? [],
      unknown_quals_count: (s.unknown_quals_count as number) ?? ((s.unknownQuals as unknown[])?.length ?? 0),
      loi,
      ir: s.exchange === 'rfg'
        ? (s.ir as number) || 0
        : (s.ir_last_block as number) || (s.ir as number) || 0,
      registered_at: (s.registered_at as string) ?? null,
      ir_last_block: (s.ir_last_block as number) ?? 0,
      currently_open_min: (s.currently_open_min as number) ?? 999,
      exchange: (s.exchange as string) ?? 'ps',
      entryPath: (s.entryPath as string) ?? undefined,
    };
  });
}

export async function prescreenSurvey(
  surveyId: string,
  qualCode: number,
  conditionCode: string,
): Promise<{ qualifies: boolean; reason?: string; unknownQuals?: Array<{ qualCode: number; question: string; type: string; options: Array<{ code: string; label: string }> }> }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/surveys/${surveyId}/prescreen`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ qualCode, conditionCode }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Prescreen failed' }));
    throw new Error(err.error ?? 'Prescreen failed');
  }
  return res.json();
}

// Unified qual-gate answer for both PS and RFG surveys. Saves the attribute
// (source='prequal') and re-runs qualification, returning the new state.
export async function answerSurveyQual(
  surveyId: string,
  attributeKey: string,
  attributeValue: string,
): Promise<{ qualifies: boolean; reason?: string; unknownQuals?: Array<{ qualCode: number; question: string; type: string; options: Array<{ code: string; label: string }>; attributeKey?: string; exchange?: string }> }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/surveys/${surveyId}/answer-qual`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ attributeKey, attributeValue }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Answer failed' }));
    throw new Error(err.error ?? 'Answer failed');
  }
  return res.json();
}

// Frequency-driven enrichment question. Returns null when all high-value
// attributes are already answered or no live qual maps to a renderable
// PROFILE_QUESTIONS entry.
export interface ProfileEnrichmentQuestion {
  attributeKey: string;
  questionText: string;
  inputType: 'single_select' | 'multi_select';
  options: Array<{ value: string; label: string }>;
  isGate: false;
}

export async function fetchProfileEnrichmentQuestion(): Promise<ProfileEnrichmentQuestion | null> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/surveys/profile-question`, { headers });
  if (!res.ok) return null;
  const body = await res.json().catch(() => null);
  return body as ProfileEnrichmentQuestion | null;
}

// Generic single-attribute save (source='prequal'). Used by the inline
// enrichment flow — never overwrites explicit/locked rows server-side.
export async function saveMemberAttribute(
  attributeKey: string,
  attributeValue: string,
): Promise<{ ok: boolean }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/member/attributes`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ attributeKey, attributeValue }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to save attribute' }));
    throw new Error(err.error ?? 'Failed to save attribute');
  }
  return res.json();
}

export async function fetchPrescreenQuestion(exclude: number[] = []): Promise<{
  question: { qualCode: number; questionText: string; questionType: string; options: Array<{ label: string; conditionCodes: string[]; value: string }>; isSensitive: boolean; surveysUnlocked: number; attributeKey: string } | null;
}> {
  const headers = await authHeaders();
  const params = exclude.length > 0 ? `?exclude=${exclude.join(',')}` : '';
  const res = await fetch(`${API_BASE}/api/surveys/prescreen-question${params}`, { headers });
  if (!res.ok) return { question: null };
  return res.json();
}

export async function submitPrescreenProfileAnswer(data: { qualCode: number; conditionCodes: string[]; attributeValue: string; attributeKey: string }): Promise<{ success: boolean }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/surveys/prescreen-answer`, {
    method: 'POST', headers, body: JSON.stringify(data),
  });
  if (!res.ok) return { success: false };
  return res.json();
}

export async function fetchPrequalGate(surveyId: string): Promise<PrequalifierQuestion[]> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/surveys/${surveyId}/prequal`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to fetch prequal questions');
  }
  const data = await res.json() as { questions: PrequalifierQuestion[] };
  return data.questions;
}

export async function submitPrequalAnswers(
  surveyId: string,
  answers: PrequalAnswer[],
): Promise<PrequalSubmitResult> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/surveys/${surveyId}/prequal`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ answers }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to submit prequal answers');
  }
  return res.json() as Promise<PrequalSubmitResult>;
}

export async function fetchRewards(): Promise<Reward[]> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/redemptions/rewards`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to fetch rewards');
  }
  const data = await res.json() as { rewards: Reward[] };
  return data.rewards;
}

export async function fetchRedemptions(
  status?: 'pending' | 'processing' | 'fulfilled' | 'failed',
): Promise<Redemption[]> {
  const headers = await authHeaders();
  const url = new URL(`${API_BASE}/api/redemptions`);
  if (status) url.searchParams.set('status', status);
  const res = await fetch(url.toString(), { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to fetch redemptions');
  }
  const data = await res.json() as { redemptions: Redemption[] };
  return data.redemptions;
}

export async function createRedemption(rewardId: string): Promise<Redemption> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/redemptions`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ reward_id: rewardId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to create redemption');
  }
  const data = await res.json() as { redemption: Redemption };
  return data.redemption;
}

export async function assignSurvey(surveyId: string): Promise<{ assignmentId: string; surveyUrl: string | null }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/surveys/${surveyId}/assign`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to assign survey');
  }
  return res.json() as Promise<{ assignmentId: string; surveyUrl: string | null }>;
}

export async function startPsSurvey(psSurveyId: string): Promise<{ entryUrl: string; sessionId: string }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/surveys/${psSurveyId}/start`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to start survey' }));
    throw new Error(err.error ?? 'Failed to start survey');
  }
  return res.json() as Promise<{ entryUrl: string; sessionId: string }>;
}

export async function startRfgSurvey(rfgId: string): Promise<{ entryUrl: string; sessionId: string }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/rfg/redirect`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ rfg_id: rfgId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to start survey' }));
    throw new Error(err.error ?? 'Failed to start survey');
  }
  return res.json() as Promise<{ entryUrl: string; sessionId: string }>;
}

export async function fetchAssignmentStatus(assignmentId: string): Promise<{
  status: string;
  pointsAwarded: number | null;
  consolationPoints: number | null;
  surveyTitle: string;
}> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/surveys/assignments/${assignmentId}/status`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to fetch status');
  }
  return res.json() as Promise<{ status: string; pointsAwarded: number | null; consolationPoints: number | null; surveyTitle: string }>;
}

export async function fetchPsSessionStatus(sessionId: string): Promise<{
  status: string;
  pointsAwarded: number | null;
  consolationPoints: number | null;
  surveyTitle: string;
}> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/surveys/sessions/${sessionId}/status`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to fetch session status');
  }
  return res.json() as Promise<{ status: string; pointsAwarded: number | null; consolationPoints: number | null; surveyTitle: string }>;
}

export async function abandonAssignment(assignmentId: string, reason: string, skipped?: boolean): Promise<{ success: boolean; consolationPoints: number }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/surveys/assignments/${assignmentId}/abandon`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ reason, skipped }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to abandon');
  }
  return res.json() as Promise<{ success: boolean; consolationPoints: number }>;
}

export async function abandonPsSession(sessionId: string, reason: string): Promise<{ success: boolean; consolationPoints: number }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/surveys/sessions/${sessionId}/abandon`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to abandon');
  }
  return res.json() as Promise<{ success: boolean; consolationPoints: number }>;
}

export async function fetchWritingPrompt(): Promise<{ prompt: string }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/member/writing-prompt`, { headers });
  if (!res.ok) throw new Error('Failed to fetch prompt');
  return res.json() as Promise<{ prompt: string }>;
}

export interface WritingSampleBehavior {
  wasPasted?: boolean;
  pastedChars?: number;
  keystrokeCount?: number;
  compositionMs?: number;
}
type WritingSampleResult = {
  passed: boolean;
  score: number;
  feedback: string;
  isLikelyAI?: boolean;
  attemptsRemaining: number;
  // What actually landed — honest reporting when a grant is withheld (paste/dup).
  pointsAwarded?: boolean;
  trustAwarded?: boolean;
};
export async function submitWritingSample(
  text: string,
  prompt: string,
  behavior?: WritingSampleBehavior,
): Promise<WritingSampleResult> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/member/writing-sample`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ text, prompt, ...(behavior ?? {}) }),
  });
  if (!res.ok) {
    return { passed: false, score: 0, feedback: 'Service unavailable. Please try again.', attemptsRemaining: 0 };
  }
  return res.json() as Promise<WritingSampleResult>;
}

export async function saveOnboardingStep(step: number, data: Record<string, unknown>): Promise<{ success: boolean; nextStep: number }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/onboarding/step/${step}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to save step');
  }
  return res.json() as Promise<{ success: boolean; nextStep: number }>;
}

export async function getOnboardingStatus(): Promise<{ onboarding_complete: boolean; onboarding_step: number; missing_attributes: string[] }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/onboarding/status`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to get onboarding status');
  }
  return res.json() as Promise<{ onboarding_complete: boolean; onboarding_step: number; missing_attributes: string[] }>;
}

export async function updateProfileAttributes(attributes: Record<string, string>): Promise<{ success: boolean; updated_attributes: string[] }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/profile/attributes`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ attributes }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to update profile');
  }
  return res.json() as Promise<{ success: boolean; updated_attributes: string[] }>;
}

export async function completeOnboarding(): Promise<{ success: boolean; points_awarded: number }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/onboarding/complete`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to complete onboarding');
  }
  return res.json() as Promise<{ success: boolean; points_awarded: number }>;
}

export async function completeProfilerModule(module: string): Promise<{ success: boolean; points_awarded: number }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/onboarding/profiler-complete`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ module }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to complete profiler module');
  }
  return res.json() as Promise<{ success: boolean; points_awarded: number }>;
}

export interface PrescreenBucket {
  label: string;
  codes: string[];
  icon?: string;
}

export async function fetchPrescreenBuckets(qualCode: number): Promise<{
  qualCode: number;
  source: 'claude' | 'fallback';
  buckets: PrescreenBucket[];
}> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/surveys/prescreen-buckets/${qualCode}`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to fetch prescreen buckets');
  }
  return res.json();
}

export async function submitPrescreenAnswer(category: string, answer: boolean): Promise<void> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/member/prescreen-answer`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ category, answer }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to submit prescreen answer');
  }
}

export async function fetchGames(options?: { device?: string; page?: number; perPage?: number }): Promise<{
  games: import('@swipeopinion/shared').DisplayGame[];
  total: number;
  page: number;
  perPage: number;
}> {
  const headers = await authHeaders();
  const params = new URLSearchParams();
  if (options?.device) params.set('device', options.device);
  if (options?.page) params.set('page', String(options.page));
  if (options?.perPage) params.set('perPage', String(options.perPage));
  const qs = params.toString();
  const res = await fetch(`${API_BASE}/api/games${qs ? `?${qs}` : ''}`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to fetch games');
  }
  return res.json() as Promise<{
    games: import('@swipeopinion/shared').DisplayGame[];
    total: number;
    page: number;
    perPage: number;
  }>;
}

export interface GokartClickResponse {
  available: boolean;
  click_url?: string;
  retry?: boolean;
  message?: string;
}

/**
 * Runs the GoKart click handshake for an offer/game. `offerId` is the raw
 * gokart_offer_id (for games, the `gokart:` prefix is stripped by the caller).
 * Sends NO body — pub_member_id + pub_device are derived server-side. Returns
 * the discriminated availability response (all are HTTP 200 from the API).
 */
export async function clickGokartOffer(offerId: string): Promise<GokartClickResponse> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/gokart/offers/${encodeURIComponent(offerId)}/click`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to open offer');
  }
  return res.json() as Promise<GokartClickResponse>;
}

export async function advancePendingPoints(ledgerIds?: string[]): Promise<{
  advanced: number;
  forfeited: number;
  new_balance: number;
}> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/member/points/advance-pending`, {
    method: 'POST',
    headers,
    body: JSON.stringify(ledgerIds && ledgerIds.length > 0 ? { ledger_ids: ledgerIds } : {}),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText, message: res.statusText }));
    const e = new Error(err.message ?? err.error ?? 'Failed to advance pending points') as Error & { code?: string };
    e.code = err.error;
    throw e;
  }
  return res.json() as Promise<{ advanced: number; forfeited: number; new_balance: number }>;
}

export async function fetchPendingSummary(): Promise<import('@swipeopinion/shared').PendingSummary> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/redemption/pending-summary`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to fetch pending summary');
  }
  return res.json() as Promise<import('@swipeopinion/shared').PendingSummary>;
}

export interface PointsLedgerResponse {
  entries: PointsLedger[];
  total: number;
  page: number;
  pageSize: number;
  // Per-category totals (unfiltered) so the history filter renders only chips
  // that have rows. Absent on older callers' expectations — always present now.
  categoryCounts?: Record<string, number>;
}

// Ship 2.5 merged history (/me/activity). A discriminated union: 'earning' rows
// are ledger entries; 'activity' rows are non-crediting survey attempts with no
// ledger row. Rendered in one list, branching on `kind`.
export type ActivityHistoryRow =
  | {
      kind: 'earning';
      id: string;
      created_at: string;
      source_type: string;
      source_id: string | null;
      delta: number;
      note: string | null;
      is_pending: boolean;
      pending_until: string | null;
    }
  | {
      kind: 'activity';
      id: string;
      created_at: string;
      status: string;
      survey_name: string;
      exchange_name: string;
      session_id: string;
    };

export interface ActivityHistoryResponse {
  rows: ActivityHistoryRow[];
  total: number;
  page: number;
  pageSize: number;
}

export async function fetchActivityHistory(page = 1): Promise<ActivityHistoryResponse> {
  const headers = await authHeaders();
  const url = new URL(`${API_BASE}/api/me/activity`);
  url.searchParams.set('page', String(page));
  const res = await fetch(url.toString(), { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to fetch activity');
  }
  return res.json() as Promise<ActivityHistoryResponse>;
}

export async function fetchPointsLedger(
  page = 1,
  category?: string,
): Promise<PointsLedgerResponse> {
  const headers = await authHeaders();
  const url = new URL(`${API_BASE}/api/me/ledger`);
  url.searchParams.set('page', String(page));
  if (category && category !== 'all') url.searchParams.set('category', category);
  const res = await fetch(url.toString(), { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to fetch ledger');
  }
  return res.json() as Promise<PointsLedgerResponse>;
}

export async function sendPhoneOtp(phoneNumber: string): Promise<{ success: boolean }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/member/phone/send-otp`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ phoneNumber }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to send verification code');
  }
  return res.json() as Promise<{ success: boolean }>;
}

export async function verifyPhoneOtp(phoneNumber: string, token: string, smsOptIn: boolean): Promise<{ success: boolean }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/member/phone/verify-otp`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ phoneNumber, token, smsOptIn }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Verification failed');
  }
  return res.json() as Promise<{ success: boolean }>;
}

export async function sendPublicPhoneOtp(phoneNumber: string): Promise<{ success: boolean }> {
  const res = await fetch(`${API_BASE}/api/auth/phone/send-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneNumber }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    if (err?.error === 'geo_blocked') {
      throw new Error(err.message ?? 'Swipe Opinion is currently available in the United States only.');
    }
    throw new Error(err.error ?? 'Failed to send verification code');
  }
  return res.json() as Promise<{ success: boolean }>;
}

export interface GeoStatus {
  isUS: boolean;
  country: string | null;
}

export async function fetchGeoStatus(token: string): Promise<GeoStatus> {
  const res = await fetch(`${API_BASE}/api/member/geo-status`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch geo status');
  return res.json() as Promise<GeoStatus>;
}

export async function verifyPublicPhoneOtp(phoneNumber: string, token: string): Promise<{ success: boolean; isNewUser: boolean; session: { access_token: string; refresh_token: string } | null }> {
  const res = await fetch(`${API_BASE}/api/auth/phone/verify-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneNumber, token }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Verification failed');
  }
  return res.json() as Promise<{ success: boolean; isNewUser: boolean; session: { access_token: string; refresh_token: string } | null }>;
}

export interface TrustHistoryEntry {
  // null when the amount is intentionally hidden from the member (e.g. support
  // adjustments — see hideAmount). Admin history is unaffected.
  delta: number | null;
  scoreAfter: number;
  label: string;
  hideAmount?: boolean;
  createdAt: string;
}

export interface TrustHistoryResponse {
  currentScore: number;
  currentLevel: number;
  levelName: string;
  history: TrustHistoryEntry[];
}

export async function fetchTrustHistory(): Promise<TrustHistoryResponse> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/trust/history`, { headers });
  if (!res.ok) throw new Error('Failed to fetch trust history');
  return res.json();
}

export async function addEmail(email: string): Promise<{ success: boolean }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/member/add-email`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to add email');
  }
  return res.json() as Promise<{ success: boolean }>;
}

// Start a Didit identity-verification session. Returns the hosted `url` to
// redirect to, or { already_verified: true } if the member is already verified,
// or { available: false } when the feature flag is off (503). Throws on other
// errors so the caller can show a friendly retry.
export async function createIdentitySession(): Promise<{ url?: string; session_id?: string; already_verified?: boolean; available?: boolean }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/member/identity/session`, {
    method: 'POST',
    headers,
    body: '{}',
  });
  handleResponse(res);
  if (res.status === 503) {
    return { available: false };
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Could not start identity verification');
  }
  return res.json() as Promise<{ url?: string; session_id?: string; already_verified?: boolean }>;
}

export async function smsOptOut(): Promise<{ success: boolean }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/member/phone/sms-opt-out`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to opt out');
  }
  return res.json() as Promise<{ success: boolean }>;
}

export async function smsOptIn(): Promise<{ success: boolean }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/member/phone/sms-opt-in`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to enable SMS notifications');
  }
  return res.json() as Promise<{ success: boolean }>;
}

export async function updateEmailPreferences(surveyFrequency: string): Promise<{ success: boolean }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/member/email-preferences`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ survey_frequency: surveyFrequency }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to update email preferences');
  }
  return res.json() as Promise<{ success: boolean }>;
}

// Onboarding incentive redesign (2026-05-11) — tiered profile score.
// score: 0..1 (rounded to 2 decimals server-side).
export interface ProfileScoreResponse {
  score: number;
  filled: number;
  denominator: number;
}

export async function fetchProfileScore(): Promise<ProfileScoreResponse> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/profile/score`, { headers });
  handleResponse(res);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to fetch profile score');
  }
  return res.json() as Promise<ProfileScoreResponse>;
}

// ── Swipe profiling deck (spec swipe-design.md §6/§7) ────────────────────────

export type SwipeDirection = 'left' | 'right' | 'skip';

export interface SwipeCard {
  key: string;
  prompt: string;
  answerModel: 'binary' | 'this_or_that';
  /** Swipe direction → canonical value (e.g. binary {left:'no',right:'yes'};
   *  this_or_that {left:'android',right:'iphone'}). Drives the on-card labels —
   *  never hardcode yes/no. */
  answerValueMap: { left: string; right: string };
}

export interface SwipeDeck {
  cards: SwipeCard[];
  /** true → member has answered every v1 attribute (empty deck). */
  allSet: boolean;
}

export interface SwipeAnswerResult {
  ok: boolean;
  recorded: 'answered' | 'skipped';
  credited: number;
  skipped?: string;
}

export async function fetchSwipeDeck(): Promise<SwipeDeck> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/swipe/deck`, { headers });
  handleResponse(res);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to load swipe deck');
  }
  return res.json() as Promise<SwipeDeck>;
}

/** Present-moment count of surveys the member currently qualifies for — the
 *  honest end-of-deck reward signal. A snapshot (inventory churns), so render it
 *  present-tense, never as a persistent "unlocked" claim. */
export async function fetchQualifyingCount(): Promise<{ count: number }> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/swipe/qualifying-count`, { headers });
  handleResponse(res);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to load qualifying count');
  }
  return res.json() as Promise<{ count: number }>;
}

export async function submitSwipeAnswer(
  cardKey: string,
  direction: SwipeDirection,
): Promise<SwipeAnswerResult> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/api/swipe/answer`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ cardKey, direction }),
  });
  handleResponse(res);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Failed to submit swipe answer');
  }
  return res.json() as Promise<SwipeAnswerResult>;
}
